import React, { useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useLazyQuery } from "@apollo/client";
import Modal from "../Model";
import { GET_REVIEW_CANDIDATES } from "./graphql";
import {
  API_BASE, img, BLOCK_TYPES, OPTION_SOURCES, DETAILS_KEYS, BULLET_STYLES, ICONS,
} from "./blocks";

const input = "border rounded-md px-3 py-2 text-sm w-full";
const small = "border rounded-md px-2 py-1.5 text-sm w-full";

const Field = ({ label, help, children }) => (
  <label className="flex flex-col gap-1 text-sm">
    <span className="font-medium">{label}</span>
    {children}
    {help && <span className="text-xs text-gray-500">{help}</span>}
  </label>
);

const Choice = ({ value, options, onChange }) => (
  <div className="flex flex-wrap gap-1.5">
    {Object.entries(options).map(([k, label]) => (
      <button
        key={k}
        type="button"
        onClick={() => onChange(k)}
        className={`px-3 py-1.5 rounded-md text-xs border ${value === k ? "bg-gray-900 text-white border-gray-900" : "bg-white hover:bg-gray-50"}`}
      >
        {label}
      </button>
    ))}
  </div>
);

const Check = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

/** Uploads to the same endpoint the service form uses and returns the stored path. */
const ImageInput = ({ value, onChange, label = "Photo" }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Max 5 MB"); return; }
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API_BASE}/img/upload`, form);
      if (data?.success) onChange(data.url); else setError("Upload failed");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 h-14 rounded-md bg-gray-100 overflow-hidden flex-none border">
        {value && <img src={img(value)} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex flex-col gap-1">
        <label className="px-3 py-1.5 rounded-md bg-gray-100 text-xs cursor-pointer hover:bg-gray-200 w-fit">
          {busy ? "Uploading…" : value ? `Change ${label.toLowerCase()}` : `Upload ${label.toLowerCase()}`}
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} />
        </label>
        {value && <button type="button" onClick={() => onChange(null)} className="text-xs text-red-600 text-left">Remove</button>}
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
    </div>
  );
};

const ImageList = ({ value = [], onChange, max = 10 }) => (
  <div className="flex flex-wrap gap-3">
    {value.map((url, i) => (
      <div key={`${url}-${i}`} className="relative w-20 h-20 rounded-md overflow-hidden border">
        <img src={img(url)} alt="" className="w-full h-full object-cover" />
        <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="absolute top-0.5 right-0.5 bg-white/90 rounded px-1 text-xs">✕</button>
        {i > 0 && (
          <button type="button" onClick={() => { const v = [...value]; [v[i - 1], v[i]] = [v[i], v[i - 1]]; onChange(v); }} className="absolute bottom-0.5 left-0.5 bg-white/90 rounded px-1 text-xs">←</button>
        )}
      </div>
    ))}
    {value.length < max && <ImageInput value={null} label="Photo" onChange={(url) => url && onChange([...value, url])} />}
  </div>
);

/** Ordered rows of objects with add / remove / move. */
const Rows = ({ value = [], onChange, blank, render, addLabel, max = 20 }) => {
  const set = (i, row) => onChange(value.map((r, k) => (k === i ? row : r)));
  const move = (i, d) => {
    const v = [...value];
    [v[i], v[i + d]] = [v[i + d], v[i]];
    onChange(v);
  };
  return (
    <div className="flex flex-col gap-2">
      {value.map((row, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="flex gap-2 items-start border rounded-md p-2 bg-gray-50">
          <span className="text-xs text-gray-400 pt-2 w-4">{i + 1}</span>
          <div className="flex-1 flex flex-col gap-1.5">{render(row, (r) => set(i, r))}</div>
          <div className="flex flex-col gap-0.5">
            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="px-1.5 text-xs disabled:opacity-30" aria-label="Move up">▲</button>
            <button type="button" disabled={i === value.length - 1} onClick={() => move(i, 1)} className="px-1.5 text-xs disabled:opacity-30" aria-label="Move down">▼</button>
            <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="px-1.5 text-xs text-red-600" aria-label="Remove">✕</button>
          </div>
        </div>
      ))}
      {value.length < max && (
        <button type="button" onClick={() => onChange([...value, { ...blank }])} className="self-start px-3 py-1.5 rounded-md border border-dashed text-xs hover:bg-gray-50">
          + {addLabel}
        </button>
      )}
    </div>
  );
};

const iconOptions = Object.entries(ICONS).map(([value, label]) => ({ value, label }));

const IconItems = ({ value, onChange, addLabel = "Add line" }) => (
  <Rows
    value={value}
    onChange={onChange}
    blank={{ icon: "check", text: "" }}
    addLabel={addLabel}
    render={(row, set) => (
      <div className="flex gap-2">
        <select className="border rounded-md px-1 text-sm w-36" value={row.icon || "check"} onChange={(e) => set({ ...row, icon: e.target.value })}>
          {iconOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input className={small} value={row.text || ""} onChange={(e) => set({ ...row, text: e.target.value })} placeholder="Short line, under 60 characters" />
      </div>
    )}
  />
);

const DetailsKeys = ({ value = [], onChange }) => (
  <div className="flex flex-wrap gap-1.5">
    {DETAILS_KEYS.map((k) => {
      const on = value.includes(k);
      return (
        <button key={k} type="button" onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])}
          className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white"}`}>
          {k}
        </button>
      );
    })}
  </div>
);

const ServicesPicker = ({ value = [], onChange, options, placeholder = "Pick services" }) => (
  <Select
    isMulti
    options={options}
    value={value.map((id) => options.find((o) => o.value === id)).filter(Boolean)}
    onChange={(v) => onChange(v.map((o) => o.value))}
    placeholder={placeholder}
  />
);

/* ---------- reviews picker ---------- */

const ReviewsPicker = ({ value = [], onChange, serviceIds, categoryIds }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [minRating, setMinRating] = useState(4);
  const [load, { data, loading }] = useLazyQuery(GET_REVIEW_CANDIDATES, { fetchPolicy: "network-only" });
  // Text of every review seen in the picker, so picked ones stay readable.
  const seen = useRef({});
  const fetchNow = (extra = {}) => load({
    variables: {
      serviceIds: serviceIds.length ? serviceIds : null,
      categoryId: !serviceIds.length && categoryIds.length ? categoryIds[0] : null,
      minRating,
      search: search || null,
      limit: 100,
      ...extra,
    },
  });
  const candidates = data?.getReviewCandidates?.reviews || [];
  candidates.forEach((r) => { seen.current[r.id] = r; });
  const picked = seen.current;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id].slice(0, 20));

  return (
    <div className="flex flex-col gap-2">
      {value.length === 0 && <p className="text-xs text-gray-500">No reviews picked. Only the rating summary shows.</p>}
      {value.map((id, i) => (
        <div key={id} className="flex items-start gap-2 border rounded-md p-2 text-xs bg-gray-50">
          <span className="text-gray-400">{i + 1}</span>
          <span className="flex-1">{picked[id] ? <>{"★".repeat(picked[id].rating)} <b>{picked[id].authorName}</b>: {picked[id].text}</> : <span className="text-gray-500">Review {id.slice(0, 8)} (open the picker to see text)</span>}</span>
          <button type="button" onClick={() => toggle(id)} className="text-red-600">✕</button>
        </div>
      ))}
      <button type="button" onClick={() => { setOpen(true); fetchNow(); }} className="self-start px-3 py-1.5 rounded-md bg-gray-100 text-xs">Pick reviews…</button>
      {open && (
        <Modal onClose={() => setOpen(false)} widthClass="sm:w-[760px]">
          <h3 className="text-lg font-semibold mb-1">Pick reviews</h3>
          <p className="text-xs text-gray-500 mb-3">
            Real reviews from completed bookings{serviceIds.length ? " of this page's services" : categoryIds.length ? " in this page's first category" : ""}.
            Customers see first name and initial only. Pick ones that are specific and kind; never edit what a customer wrote.
          </p>
          <div className="flex gap-2 mb-3">
            <input className={small} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search text (e.g. wax, hygienic)" />
            <select className="border rounded-md px-2 text-sm" value={minRating} onChange={(e) => setMinRating(Number(e.target.value))}>
              {[5, 4, 3].map((n) => <option key={n} value={n}>{n}★ and up</option>)}
            </select>
            <button type="button" onClick={() => fetchNow()} className="px-3 py-1.5 rounded-md bg-gray-900 text-white text-sm">Search</button>
          </div>
          {loading && <p className="text-sm text-gray-500">Loading…</p>}
          {!loading && !candidates.length && <p className="text-sm text-gray-500">No reviews with text match. Try a lower rating or clear the search.</p>}
          <ul className="divide-y max-h-[55vh] overflow-y-auto border rounded-md">
            {candidates.map((r) => (
              <li key={r.id} className="flex gap-3 p-2 text-sm">
                <input type="checkbox" className="mt-1" checked={value.includes(r.id)} onChange={() => toggle(r.id)} aria-label="Pick review" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">
                    <span className="text-amber-500">{"★".repeat(r.rating)}</span> {r.authorName} · {r.date ? new Date(r.date).toLocaleDateString("en-IN") : ""} · {r.serviceSummary}
                  </div>
                  <div>{r.text}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex justify-end mt-3">
            <button type="button" onClick={() => setOpen(false)} className="px-5 py-2 rounded-md bg-[#FBB033] font-semibold">Done ({value.length} picked)</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ---------- per type ---------- */

/**
 * Edits one block. `ctx` holds the pickers' options:
 * { serviceOptions, collections, pages, pageId, serviceIds, categoryIds }.
 */
const BlockForm = ({ block, onChange, ctx }) => {
  const c = block.config || {};
  const setC = (patch) => onChange({ ...block, config: { ...c, ...patch } });
  const setB = (patch) => onChange({ ...block, ...patch });
  const titleField = (placeholder) => (
    <Field label="Title" help="Leave empty to use the default.">
      <input className={input} value={block.title || ""} onChange={(e) => setB({ title: e.target.value || null })} placeholder={placeholder} />
    </Field>
  );
  const subtitleField = (
    <Field label="Subtitle (optional)">
      <input className={input} value={block.subtitle || ""} onChange={(e) => setB({ subtitle: e.target.value || null })} />
    </Field>
  );

  let body;
  switch (block.type) {
    case "MEDIA":
      body = (
        <>
          <Field label="Photos" help="4:3 works best. Up to 10."><ImageList value={c.images} onChange={(images) => setC({ images })} /></Field>
          <Check checked={c.useServiceImage !== false} onChange={(v) => setC({ useServiceImage: v })} label="Use the service's own photo when no photos are added here" />
          <Field label="Shape"><Choice value={c.aspect || "4:3"} options={{ "4:3": "4:3", "16:9": "16:9 wide", "1:1": "Square" }} onChange={(aspect) => setC({ aspect })} /></Field>
        </>
      );
      break;
    case "HIGHLIGHTS":
      body = (
        <>
          <Field label="Style"><Choice value={c.style || "LIST"} options={{ LIST: "List with icons", BANNER: "Banner", CHIPS: "Chips" }} onChange={(style) => setC({ style })} /></Field>
          {c.style !== "CHIPS" && (
            <Field label="Headline"><input className={input} value={c.headline || ""} onChange={(e) => setC({ headline: e.target.value })} placeholder="India's first Korean waxing ritual" /></Field>
          )}
          {c.style === "BANNER" && (
            <>
              <Field label="Body (optional)"><textarea rows={2} className={input} value={c.body || ""} onChange={(e) => setC({ body: e.target.value })} /></Field>
              <Field label="Photo (optional, shown under the text)"><ImageInput value={c.image} onChange={(image) => setC({ image })} /></Field>
              <div className="flex gap-4">
                <Field label="Background"><input type="color" value={c.background || "#F3EEFE"} onChange={(e) => setC({ background: e.target.value })} /></Field>
                <Field label="Text & icon colour"><input type="color" value={c.accent || "#5A36C4"} onChange={(e) => setC({ accent: e.target.value })} /></Field>
              </div>
            </>
          )}
          {c.style === "CHIPS" && titleField("")}
          <Field label="Lines"><IconItems value={c.items} onChange={(items) => setC({ items })} /></Field>
        </>
      );
      break;
    case "OPTIONS":
      body = (
        <>
          {titleField("Choose your option")}
          <Field label="Which services">
            <select className={input} value={c.source || "VARIANTS"} onChange={(e) => setC({ source: e.target.value })}>
              {Object.entries(OPTION_SOURCES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          {c.source === "COLLECTION" && (
            <Field label="Collection">
              <select className={input} value={c.collectionId || ""} onChange={(e) => setC({ collectionId: e.target.value })}>
                <option value="">Pick a collection</option>
                {ctx.collections.map((col) => <option key={col.id} value={col.id}>{col.name}</option>)}
              </select>
            </Field>
          )}
          {c.source === "SERVICES" && <Field label="Services"><ServicesPicker value={c.serviceIds} onChange={(serviceIds) => setC({ serviceIds })} options={ctx.serviceOptions} /></Field>}
          <Field label="Card label" help="Wax type reads better than the full name when every option is the same area.">
            <Choice value={c.labelBy || "NAME"} options={{ PRODUCT: "Product / wax type", NAME: "Service name" }} onChange={(labelBy) => setC({ labelBy })} />
          </Field>
          <Field label="Most cards"><input type="number" min={2} max={20} className={input} value={c.max || 10} onChange={(e) => setC({ max: Number(e.target.value) })} /></Field>
        </>
      );
      break;
    case "INCLUDES":
      body = <>{titleField("What's included")}{subtitleField}</>;
      break;
    case "STEPS": {
      const fromDetails = c.source === "DETAILS";
      body = (
        <>
          {titleField("How it works")}
          <Field label="Style"><Choice value={c.style || "TIMELINE"} options={{ TIMELINE: "Timeline", CAROUSEL: "Swipe cards" }} onChange={(style) => setC({ style })} /></Field>
          <Field label="Steps come from">
            <Choice value={c.source || "CUSTOM"} options={{ DETAILS: "Each service's details", CUSTOM: "Written here" }} onChange={(source) => setC({ source })} />
          </Field>
          {fromDetails && <Field label="Details sections"><DetailsKeys value={c.detailsKeys || ["Procedure"]} onChange={(detailsKeys) => setC({ detailsKeys })} /></Field>}
          <Field label={fromDetails ? "Titles and photos per step (optional)" : "Steps"} help={fromDetails ? "Step 1 here adds a title / photos to the service's first line, and so on. Text comes from the service." : null}>
            <Rows
              value={c.steps}
              onChange={(steps) => setC({ steps })}
              blank={{ title: "", body: "", images: [] }}
              addLabel="Add step"
              max={12}
              render={(row, set) => (
                <>
                  <input className={small} value={row.title || ""} onChange={(e) => set({ ...row, title: e.target.value })} placeholder="Title, e.g. Pre-wax preparation" />
                  {!fromDetails && <textarea rows={2} className={small} value={row.body || ""} onChange={(e) => set({ ...row, body: e.target.value })} placeholder="What happens in this step" />}
                  <ImageList value={row.images || []} onChange={(images) => set({ ...row, images })} max={2} />
                </>
              )}
            />
          </Field>
        </>
      );
      break;
    }
    case "BEFORE_AFTER":
      body = (
        <>
          {titleField("See the difference")}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Before"><ImageInput value={c.before} onChange={(before) => setC({ before })} /></Field>
            <Field label="After"><ImageInput value={c.after} onChange={(after) => setC({ after })} /></Field>
          </div>
          <Field label="Caption (optional)" help="Say whose photos these are, e.g. 'Real Barbera customer, after 1 session'."><input className={input} value={c.caption || ""} onChange={(e) => setC({ caption: e.target.value })} /></Field>
        </>
      );
      break;
    case "PRODUCTS":
      body = (
        <>
          {titleField("Products we use")}
          <Field label="Products come from"><Choice value={c.source || "SERVICE"} options={{ SERVICE: "The service (Products used)", CUSTOM: "Listed here" }} onChange={(source) => setC({ source })} /></Field>
          {c.source === "CUSTOM" && (
            <Rows value={c.items} onChange={(items) => setC({ items })} blank={{ name: "", imageUrl: null }} addLabel="Add product" max={12}
              render={(row, set) => (
                <div className="flex gap-2 items-center">
                  <input className={small} value={row.name || ""} onChange={(e) => set({ ...row, name: e.target.value })} placeholder="Rica Liposoluble Wax" />
                  <ImageInput value={row.imageUrl} onChange={(imageUrl) => set({ ...row, imageUrl })} />
                </div>
              )}
            />
          )}
        </>
      );
      break;
    case "BULLETS":
      body = (
        <>
          {titleField("Please note")}
          <Field label="Style">
            <select className={input} value={c.style || "CHECK"} onChange={(e) => setC({ style: e.target.value })}>
              {Object.entries(BULLET_STYLES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Lines come from"><Choice value={c.source || "CUSTOM"} options={{ DETAILS: "Each service's details", CUSTOM: "Written here" }} onChange={(source) => setC({ source })} /></Field>
          {c.source === "DETAILS"
            ? <Field label="Details sections" help="Lines from these sections are merged, without repeats."><DetailsKeys value={c.detailsKeys} onChange={(detailsKeys) => setC({ detailsKeys })} /></Field>
            : <Field label="Lines"><IconItems value={c.items} onChange={(items) => setC({ items })} /></Field>}
        </>
      );
      break;
    case "FAQ":
      body = (
        <>
          {titleField("Frequently asked questions")}
          <Rows value={c.items} onChange={(items) => setC({ items })} blank={{ q: "", a: "" }} addLabel="Add question" max={15}
            render={(row, set) => (
              <>
                <input className={small} value={row.q || ""} onChange={(e) => set({ ...row, q: e.target.value })} placeholder="Question" />
                <textarea rows={2} className={small} value={row.a || ""} onChange={(e) => set({ ...row, a: e.target.value })} placeholder="Answer" />
              </>
            )}
          />
        </>
      );
      break;
    case "INFO_SHEET":
      body = (
        <>
          <Field label="Row label"><input className={input} value={c.label || ""} onChange={(e) => setC({ label: e.target.value })} placeholder="Know your wax type" /></Field>
          <Field label="Sheet title"><input className={input} value={c.sheetTitle || ""} onChange={(e) => setC({ sheetTitle: e.target.value })} placeholder="Which wax is right for you?" /></Field>
          <Field label="Sheet intro (optional)"><textarea rows={2} className={input} value={c.body || ""} onChange={(e) => setC({ body: e.target.value })} /></Field>
          <Field label="Cards in the sheet">
            <Rows value={c.items} onChange={(items) => setC({ items })} blank={{ title: "", body: "", image: null }} addLabel="Add card" max={10}
              render={(row, set) => (
                <>
                  <input className={small} value={row.title || ""} onChange={(e) => set({ ...row, title: e.target.value })} placeholder="Rica wax" />
                  <textarea rows={2} className={small} value={row.body || ""} onChange={(e) => set({ ...row, body: e.target.value })} placeholder="Who it suits" />
                  <ImageInput value={row.image} onChange={(image) => set({ ...row, image })} />
                </>
              )}
            />
          </Field>
        </>
      );
      break;
    case "TRUST":
      body = (
        <>
          <Field label="Headline"><input className={input} value={c.headline || ""} onChange={(e) => setC({ headline: e.target.value })} placeholder="The Barbera promise" /></Field>
          <Check checked={c.showStats !== false} onChange={(v) => setC({ showStats: v })} label="Show real totals (average rating, services done, customers)" />
          <Field label="Photo (optional)" help="A real Barbera beautician in uniform, with her consent."><ImageInput value={c.image} onChange={(image) => setC({ image })} /></Field>
          <Field label="Promises" help="Leave empty for the standard four."><IconItems value={c.points} onChange={(points) => setC({ points })} addLabel="Add promise" /></Field>
        </>
      );
      break;
    case "REVIEWS":
      body = (
        <>
          {titleField("Ratings & reviews")}
          <Check checked={c.showSummary !== false} onChange={(v) => setC({ showSummary: v })} label="Show the rating summary (needs 10+ ratings)" />
          <Field label="Reviews to show">
            <ReviewsPicker value={c.orderIds} onChange={(orderIds) => setC({ orderIds })} serviceIds={ctx.serviceIds} categoryIds={ctx.categoryIds} />
          </Field>
        </>
      );
      break;
    case "ALSO_ADD":
      body = (
        <>
          {titleField("Frequently added together")}
          <Field label="Services"><Choice value={c.source || "AUTO"} options={{ AUTO: "Automatic (booked in the same order)", SERVICES: "I pick" }} onChange={(source) => setC({ source })} /></Field>
          {c.source === "SERVICES" && <ServicesPicker value={c.serviceIds} onChange={(serviceIds) => setC({ serviceIds })} options={ctx.serviceOptions} />}
        </>
      );
      break;
    case "INCLUDE_PAGE":
      body = (
        <Field label="Page to insert" help="Its blocks appear here in its order. Pages inside it are not inserted again.">
          <select className={input} value={c.pageId || ""} onChange={(e) => setC({ pageId: e.target.value })}>
            <option value="">Pick a page</option>
            {ctx.pages.filter((p) => p.id !== ctx.pageId && p.status !== "ARCHIVED").map((p) => <option key={p.id} value={p.id}>{p.name} ({p.status.toLowerCase()})</option>)}
          </select>
        </Field>
      );
      break;
    default:
      body = null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-semibold">{BLOCK_TYPES[block.type]?.label || block.type}</h3>
        <p className="text-xs text-gray-500">{BLOCK_TYPES[block.type]?.help}</p>
      </div>
      {body}
    </div>
  );
};

export default BlockForm;
